import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { updateProfile, claimOrdersByPhone } from '../../services/accountApi';
import {
  birthNumberFromDate,
  planetFromNumber,
  PLANET_EMOJI,
} from '../../utils/astrology';
import {
  STATES,
  EMPTY_ADDRESS,
  addressFromProfile,
  addressToColumns,
  validateAddress,
  isAddressComplete,
} from '../../utils/address';

export default function AccountProfile() {
  const { user, profile, refreshProfile } = useAuth();

  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    birth_date: '',
  });
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        birth_date: profile.birth_date || '',
      });
      setAddress(addressFromProfile(profile));
    }
  }, [profile]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setAddress((a) => ({ ...a, [k]: v }));

  // Live preview so the customer sees their planet before saving.
  const previewNumber = birthNumberFromDate(form.birth_date);
  const previewPlanet = planetFromNumber(previewNumber);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.full_name.trim()) return setError('Please enter your name.');

    // The address is optional here, but if it's been started it must be
    // complete — a half-filled address is worse than none at delivery time.
    const addressTouched = Object.values(address).some((v) => String(v).trim());
    if (addressTouched) {
      const addressError = validateAddress(address);
      if (addressError) return setError(addressError);
    }

    setBusy(true);
    const { error } = await updateProfile(user.id, user.email, {
      full_name: form.full_name.trim(),
      phone: form.phone.trim() || null,
      birth_date: form.birth_date || null,
      ...(addressTouched ? addressToColumns(address) : {}),
    });

    if (error) {
      setBusy(false);
      const rls = /row-level security|violates row/i.test(error.message || '');
      return setError(
        rls
          ? 'Your account is missing database permissions. Run ' +
            'src/backend/sql/customer_accounts.sql in the Supabase SQL Editor, ' +
            'then try again.'
          : error.message
      );
    }

    // Link any guest/WhatsApp orders placed with this phone number.
    let claimedMsg = '';
    if (form.phone.trim()) {
      const { claimed } = await claimOrdersByPhone(form.phone.trim());
      if (claimed > 0) {
        claimedMsg = ` We also linked ${claimed} earlier order${
          claimed === 1 ? '' : 's'
        } placed with this number.`;
      }
    }

    await refreshProfile();
    setBusy(false);
    setSuccess(`Details saved.${claimedMsg}`);
  };

  return (
    <div>
      <h3 className="account-h3">My Details</h3>

      <form className="account-form boxed" onSubmit={handleSubmit}>
        <label>
          Email
          <input value={user?.email || ''} disabled />
          <span className="field-hint">
            Your sign-in email can't be changed here — contact us if you need
            it updated.
          </span>
        </label>

        <label>
          Full Name
          <input
            value={form.full_name}
            onChange={(e) => set('full_name', e.target.value)}
          />
        </label>

        <label>
          Phone (WhatsApp)
          <input
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="+91 99999 00000"
          />
          <span className="field-hint">
            We use this to link orders you placed over WhatsApp before signing
            up.
          </span>
        </label>

        <label>
          Date of Birth
          <input
            type="date"
            value={form.birth_date}
            onChange={(e) => set('birth_date', e.target.value)}
          />
          <span className="field-hint">
            Used to calculate your birth number and ruling planet.
          </span>
        </label>

        {previewPlanet && (
          <div className="planet-preview">
            <span className="planet-preview-icon">
              {PLANET_EMOJI[previewPlanet] || '💎'}
            </span>
            <div>
              <strong>{previewPlanet}</strong>
              <span className="mini-sub"> · birth number {previewNumber}</span>
            </div>
          </div>
        )}

        {/* ---------- delivery address ---------- */}
        <p className="account-form-head">
          Delivery Address
          {isAddressComplete(address) && (
            <span className="saved-tag">Saved</span>
          )}
        </p>
        <span className="field-hint address-intro">
          Save it once and checkout will fill it in for you.
        </span>

        <label>
          Flat / House No, Building
          <input
            value={address.house}
            onChange={(e) => setAddr('house', e.target.value)}
            placeholder="e.g. B-402, Shanti Apartments"
            autoComplete="address-line1"
          />
        </label>

        <label>
          Street / Area / Locality
          <input
            value={address.street}
            onChange={(e) => setAddr('street', e.target.value)}
            placeholder="e.g. Green Park Extension"
            autoComplete="address-line2"
          />
        </label>

        <label>
          Landmark <span className="optional-note">(optional)</span>
          <input
            value={address.landmark}
            onChange={(e) => setAddr('landmark', e.target.value)}
            placeholder="e.g. Opposite Metro Station"
          />
        </label>

        <div className="form-two">
          <label>
            City
            <input
              value={address.city}
              onChange={(e) => setAddr('city', e.target.value)}
              placeholder="New Delhi"
              autoComplete="address-level2"
            />
          </label>

          <label>
            PIN Code
            <input
              value={address.pincode}
              onChange={(e) =>
                setAddr('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="110016"
              inputMode="numeric"
              autoComplete="postal-code"
            />
          </label>
        </div>

        <label>
          State
          <select
            value={address.state}
            onChange={(e) => setAddr('state', e.target.value)}
            autoComplete="address-level1"
          >
            <option value="">Select state</option>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        {error && <p className="account-error">{error}</p>}
        {success && <p className="account-success">{success}</p>}

        <button className="account-btn" disabled={busy}>
          {busy ? 'Saving…' : 'Save Details'}
        </button>
      </form>
    </div>
  );
}
